if (!customElements.get('jjc-music-player')) {
  class JJCMusicPlayer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.audio = this.querySelector('audio[data-audio]');
      this.stage = this.querySelector('[data-player-stage]');
      this.empty = this.querySelector('[data-player-empty]');
      this.carousel = this.querySelector('.jjc-player__releases');
      const releaseNodesByHandle = new Map();
      this.releaseNodes = [];
      Array.from(this.querySelectorAll('[data-jjc-release]')).forEach((node) => {
        const handle = this.slugify(node.dataset.title);
        if (!handle || !releaseNodesByHandle.has(handle)) {
          if (handle) releaseNodesByHandle.set(handle, node);
          this.releaseNodes.push(node);
          return;
        }

        const existing = releaseNodesByHandle.get(handle);
        const preferred = node.dataset.releaseSource === 'metaobject' ? node : existing;
        const fallback = preferred === node ? existing : node;
        this.mergeReleaseFallback(preferred, fallback);

        if (preferred !== existing) {
          const existingIndex = this.releaseNodes.indexOf(existing);
          if (existingIndex >= 0) this.releaseNodes.splice(existingIndex, 1, preferred);
          releaseNodesByHandle.set(handle, preferred);
        }
        fallback.remove();
      });
      this.releases = this.releaseNodes.map((node) => this.readRelease(node));
      this.currentReleaseIndex = 0;
      this.currentTrackIndex = 0;
      this.autoplayNext = this.dataset.autoplayNext !== 'false';
      this.loopLibrary = this.dataset.loopLibrary !== 'false';
      this.wasPlayingBeforeLoad = false;
      this.explicitAllowed = this.readExplicitPreference();
      this.explicitPreferenceKey = 'jjc_explicit_content_allowed';
      this.libraryOpen = false;
      this.releaseBrowserOpen = this.dataset.releaseDrawerOpen !== 'false';
      this.releaseBrowserAutoClose = this.dataset.releaseDrawerAutoClose !== 'false';
      this.searchQuery = '';
      this.releaseDrawerTimer = null;
      this.playbackIntentAt = 0;
      this.insertOpen = false;
      this.insertPageIndex = 0;

      this.cacheElements();
      this.bindEvents();
      this.setReleaseBrowserOpen(this.releaseBrowserOpen);

      if (!this.releases.length) {
        this.empty.hidden = false;
        return;
      }

      this.stage.hidden = false;
      const requestedParams = new URLSearchParams(window.location.search);
      const requestedRelease = requestedParams.get('release');
      const requestedAutoplay = requestedParams.get('autoplay') === '1';
      const requestedTrack = Math.max(0, Number(requestedParams.get('track') || 1) - 1);
      const requestedIndex = requestedRelease
        ? this.releases.findIndex((release) => this.slugify(release.title) === this.slugify(requestedRelease))
        : -1;
      this.selectRelease(requestedIndex >= 0 ? requestedIndex : 0, {
        autoplay: requestedAutoplay,
        focusStage: false,
        trackIndex: requestedTrack
      });
      if ('ResizeObserver' in window) {
        this.marqueeObserver = new ResizeObserver(() => this.updateMarquee());
        this.marqueeObserver.observe(this.trackTitle);
      }
    }

    mergeReleaseFallback(preferred, fallback) {
      if (!preferred || !fallback || preferred.dataset.releaseSource !== 'metaobject') return;

      const preferredTracks = Array.from(preferred.querySelectorAll('[data-jjc-track]'));
      const hasPlayableTracks = preferredTracks.some((track) => track.dataset.audioUrl);
      const preferredBulk = preferred.querySelector('[data-bulk-tracks]');
      const fallbackBulk = fallback.querySelector('[data-bulk-tracks]');
      if (!hasPlayableTracks && !preferredBulk?.value?.trim() && fallbackBulk?.value?.trim()) {
        preferred.append(fallbackBulk.cloneNode(true));
      }

      const preferredNotes = preferred.querySelector('[data-release-notes-json]');
      const fallbackNotes = fallback.querySelector('[data-release-notes-json]');
      const preferredNotesValue = preferredNotes?.textContent?.trim();
      if (preferredNotes && fallbackNotes && (!preferredNotesValue || preferredNotesValue === '""')) {
        preferredNotes.textContent = fallbackNotes.textContent;
      }

      if (!preferred.querySelector('[data-jjc-insert-slide]')) {
        fallback.querySelectorAll('[data-jjc-insert-slide]').forEach((slide) => {
          preferred.append(slide.cloneNode(true));
        });
      }
    }

    cacheElements() {
      this.activeArt = this.querySelector('[data-active-art]');
      this.nowPlaying = this.querySelector('.jjc-player__now-playing');
      this.stageBackground = this.querySelector('[data-stage-background]');
      this.releaseMeta = this.querySelector('[data-release-meta]');
      this.releaseTitle = this.querySelector('[data-release-title]');
      this.trackTitle = this.querySelector('[data-track-title]');
      this.trackTitleText = this.querySelector('[data-track-title-text]');
      this.trackPosition = this.querySelector('[data-track-position]');
      this.playToggle = this.querySelector('[data-play-toggle]');
      this.playIcon = this.querySelector('[data-play-icon]');
      this.pauseIcon = this.querySelector('[data-pause-icon]');
      this.progress = this.querySelector('[data-progress]');
      this.currentTime = this.querySelector('[data-current-time]');
      this.duration = this.querySelector('[data-duration]');
      this.serviceLinks = this.querySelector('[data-service-links]');
      this.trackList = this.querySelector('[data-track-list]');
      this.trackListHeading = this.querySelector('[data-track-list-heading]');
      this.releaseNotes = this.querySelector('[data-release-notes]');
      this.detailsToggle = this.querySelector('[data-details-toggle]');
      this.libraryPanel = this.querySelector('[data-library-panel]');
      this.libraryToggle = this.querySelector('[data-library-toggle]');
      this.libraryClose = this.querySelector('[data-library-close]');
      this.drawerNowPlaying = this.querySelector('[data-drawer-now-playing]');
      this.libraryGrabber = this.querySelector('[data-library-grabber]');
      this.featuredSlot = this.querySelector('[data-featured-slot]');
      this.releaseBrowser = this.querySelector('[data-release-browser]');
      this.releaseBrowserPanel = this.querySelector('[data-release-browser-panel]');
      this.releaseBrowserToggle = this.querySelector('[data-release-browser-toggle]');
      this.releaseBrowserOpenButton = this.querySelector('[data-release-browser-open]');
      this.releaseBrowserLabel = this.querySelector('[data-release-browser-label]');
      this.catalogSearch = this.querySelector('[data-catalog-search]');
      this.searchClear = this.querySelector('[data-search-clear]');
      this.searchStatus = this.querySelector('[data-search-status]');
      this.searchEmpty = this.querySelector('[data-search-empty]');
      this.releaseCarousel = this.querySelector('[data-release-carousel]');
      this.searchResults = this.querySelector('[data-search-results]');
      this.insertPanel = this.querySelector('[data-insert-panel]');
      this.insertOpenButton = this.querySelector('[data-insert-open]');
      this.insertClose = this.querySelector('[data-insert-close]');
      this.insertHeading = this.querySelector('[data-insert-heading]');
      this.insertBook = this.querySelector('[data-insert-book]');
      this.insertLeft = this.querySelector('[data-insert-left]');
      this.insertRight = this.querySelector('[data-insert-right]');
      this.insertPrevious = this.querySelector('[data-insert-previous]');
      this.insertNext = this.querySelector('[data-insert-next]');
      this.insertMiniArt = this.querySelector('[data-insert-mini-art]');
      this.insertReleaseTitle = this.querySelector('[data-insert-release-title]');
      this.insertTrackTitle = this.querySelector('[data-insert-track-title]');
      this.insertPreviousTrack = this.querySelector('[data-insert-previous-track]');
      this.insertPlay = this.querySelector('[data-insert-play]');
      this.insertPlayIcon = this.querySelector('[data-insert-play-icon]');
      this.insertPauseIcon = this.querySelector('[data-insert-pause-icon]');
      this.insertNextTrack = this.querySelector('[data-insert-next-track]');
      this.insertPageCount = this.querySelector('[data-insert-page-count]');
      this.insertTrackPosition = this.querySelector('[data-insert-track-position]');
      this.insertImageViewer = this.querySelector('[data-insert-image-viewer]');
      this.insertImageFull = this.querySelector('[data-insert-image-full]');
      this.insertImageCloseButtons = Array.from(this.querySelectorAll('[data-insert-image-close]'));
      this.explicitToggle = this.querySelector('[data-explicit-toggle]');
      this.explicitDialog = this.querySelector('[data-explicit-dialog]');
      this.explicitConfirm = this.querySelector('[data-explicit-confirm]');
      this.explicitCancelButtons = Array.from(this.querySelectorAll('[data-explicit-cancel]'));
    }

    bindEvents() {
      this.releaseNodes.forEach((node, index) => {
        node.querySelector('.jjc-player__release-select')?.addEventListener('click', () => {
          this.selectRelease(index, {
            autoplay: false,
            focusStage: false,
            fromUser: true
          });
          this.setReleaseBrowserOpen(false);
        });
      });

      this.querySelector('[data-carousel-previous]')?.addEventListener('click', () => this.moveRelease(-1));
      this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.moveRelease(1));
      this.querySelector('[data-previous-track]')?.addEventListener('click', () => this.previousTrack());
      this.querySelector('[data-next-track]')?.addEventListener('click', () => this.nextTrack(false));
      this.playToggle?.addEventListener('click', () => this.togglePlayback());
      this.libraryToggle?.addEventListener('click', () => this.setLibraryOpen(!this.libraryOpen));
      this.libraryClose?.addEventListener('click', () => this.setLibraryOpen(false));
      this.libraryGrabber?.addEventListener('click', () => this.setLibraryOpen(true));
      this.insertOpenButton?.addEventListener('click', () => this.setInsertOpen(true));
      this.insertClose?.addEventListener('click', () => this.setInsertOpen(false));
      this.insertPrevious?.addEventListener('click', () => this.moveInsertPages(-1));
      this.insertNext?.addEventListener('click', () => this.moveInsertPages(1));
      this.insertPreviousTrack?.addEventListener('click', () => this.previousTrack());
      this.insertPlay?.addEventListener('click', () => this.togglePlayback());
      this.insertNextTrack?.addEventListener('click', () => this.nextTrack(false));
      this.insertImageCloseButtons.forEach((button) => button.addEventListener('click', () => this.closeInsertImage()));
      this.insertImageViewer?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeInsertImage();
      });
      this.insertImageFull?.addEventListener('click', () => {
        this.insertImageFull.classList.toggle('is-zoomed');
      });
      this.releaseBrowserToggle?.addEventListener('click', () => this.setReleaseBrowserOpen(!this.releaseBrowserOpen));
      this.releaseBrowserOpenButton?.addEventListener('click', () => this.setReleaseBrowserOpen(true, { focus: true }));
      this.releaseBrowser?.addEventListener('pointerleave', (event) => {
        if (this.releaseBrowserOpen && this.stage.contains(event.relatedTarget)) {
          this.scheduleReleaseDrawerClose({ requirePlaying: false });
        }
      });
      this.releaseBrowser?.addEventListener('pointerenter', () => this.clearReleaseDrawerTimer());
      this.stage?.addEventListener('click', (event) => {
        if (this.releaseBrowserOpen && !this.releaseBrowser.contains(event.target)) {
          this.setReleaseBrowserOpen(false);
        }
        if (
          this.libraryOpen
          && !this.libraryPanel.contains(event.target)
          && !event.target.closest('[data-library-toggle]')
          && !event.target.closest('[data-library-grabber]')
        ) {
          this.setLibraryOpen(false, { restoreFocus: false });
        }
      });
      this.catalogSearch?.addEventListener('input', () => this.filterCatalog(this.catalogSearch.value));
      this.searchClear?.addEventListener('click', () => {
        this.catalogSearch.value = '';
        this.filterCatalog('');
        this.catalogSearch.focus();
      });
      this.explicitToggle?.addEventListener('click', () => {
        if (this.explicitAllowed) this.setExplicitAllowed(false);
        else this.openExplicitDialog();
      });
      this.explicitConfirm?.addEventListener('click', () => {
        const pendingTrackIndex = this.pendingExplicitTrackIndex;
        this.setExplicitAllowed(true);
        this.closeExplicitDialog();
        if (Number.isInteger(pendingTrackIndex)) {
          this.currentTrackIndex = pendingTrackIndex;
          this.loadCurrentTrack(true);
        }
      });
      this.explicitCancelButtons.forEach((button) => button.addEventListener('click', () => this.closeExplicitDialog()));

      this.progress?.addEventListener('input', () => {
        if (!Number.isFinite(this.audio.duration) || this.audio.duration <= 0) return;
        this.audio.currentTime = (Number(this.progress.value) / 1000) * this.audio.duration;
      });

      this.audio?.addEventListener('loadedmetadata', () => this.updateTimeline());
      this.audio?.addEventListener('durationchange', () => this.updateTimeline());
      this.audio?.addEventListener('timeupdate', () => this.updateTimeline());
      this.audio?.addEventListener('play', () => {
        this.insertPanel?.querySelectorAll('audio').forEach((memoryAudio) => {
          if (!memoryAudio.paused) {
            memoryAudio.dataset.resumeMain = 'false';
            memoryAudio.pause();
          }
        });
        this.setPlayingState(true);
        if (Date.now() - this.playbackIntentAt < 2000) this.scheduleReleaseDrawerClose();
      });
      this.audio?.addEventListener('pause', () => {
        this.setPlayingState(false);
        this.clearReleaseDrawerTimer();
      });
      this.audio?.addEventListener('ended', () => {
        if (this.autoplayNext) this.nextTrack(true);
        else this.setPlayingState(false);
      });

      this.detailsToggle?.addEventListener('click', () => {
        const expanded = this.detailsToggle.getAttribute('aria-expanded') === 'true';
        this.detailsToggle.setAttribute('aria-expanded', String(!expanded));
        this.releaseNotes.hidden = expanded;
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (!this.explicitDialog.hidden) this.closeExplicitDialog();
        else if (this.insertImageViewer && !this.insertImageViewer.hidden) this.closeInsertImage();
        else if (this.insertOpen) this.setInsertOpen(false);
        else if (this.libraryOpen) this.setLibraryOpen(false);
        else if (this.releaseBrowserOpen) this.setReleaseBrowserOpen(false);
      });

      window.addEventListener('pagehide', () => this.resetOverlays());

      document.addEventListener('shopify:block:select', (event) => {
        const index = this.releaseNodes.findIndex((node) => node.contains(event.target));
        if (index >= 0) {
          this.setReleaseBrowserOpen(true);
          this.selectRelease(index, { autoplay: false, focusStage: false });
        }
      });
    }

    readRelease(node) {
      const explicitTracks = Array.from(node.querySelectorAll('[data-jjc-track]'))
        .map((track) => ({
          title: track.dataset.title || 'Untitled track',
          url: track.dataset.audioUrl || '',
          credits: track.dataset.credits || '',
          explicit: track.dataset.explicit === 'true'
        }))
        .filter((track) => track.url);

      const bulkSource = node.querySelector('[data-bulk-tracks]')?.value || '';
      const bulkTracks = bulkSource.split(/\r?\n/).map((line) => {
        const match = line.trim().match(/^(.*?),\s*(https?:\/\/[^,\s]+)(?:,\s*(explicit|clean))?\s*$/i);
        if (!match) return null;
        const legacyExplicit = /🅴|\[explicit\]/i.test(match[1]);
        const title = match[1].replace(/\s*(?:🅴|\[explicit\])\s*/gi, ' ').trim();
        return { title, url: match[2].trim(), credits: '', explicit: legacyExplicit || match[3]?.toLowerCase() === 'explicit' };
      }).filter(Boolean);

      let notes = '';
      try {
        notes = JSON.parse(node.querySelector('[data-release-notes-json]')?.textContent || '""') || '';
      } catch (error) {
        notes = '';
      }

      const slides = Array.from(node.querySelectorAll('[data-jjc-insert-slide]')).map((slide) => {
        let content = '';
        try {
          content = JSON.parse(slide.querySelector('[data-slide-content]')?.textContent || '""') || '';
        } catch (error) {
          content = '';
        }
        return {
          kind: slide.dataset.kind || 'text',
          title: slide.dataset.title || '',
          image: slide.dataset.image || '',
          video: slide.dataset.video || '',
          audio: slide.dataset.slideAudio || '',
          content
        };
      });

      return {
        node,
        id: node.dataset.releaseId,
        title: node.dataset.title || 'Untitled release',
        type: node.dataset.releaseType || 'Release',
        date: node.dataset.releaseDate || '',
        cover: node.dataset.cover || '',
        featuredTrack: Math.max(0, Number(node.dataset.featuredTrack || 1) - 1),
        apple: node.dataset.apple || '',
        spotify: node.dataset.spotify || '',
        youtube: node.dataset.youtube || '',
        purchase: node.dataset.purchase || '',
        purchaseLabel: node.dataset.purchaseLabel || 'Buy high-quality download',
        notes,
        slides,
        tracks: explicitTracks.length ? explicitTracks : bulkTracks
      };
    }

    slugify(value) {
      return String(value || '')
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    }

    selectRelease(index, options = {}) {
      const release = this.releases[index];
      if (!release) return;

      const currentWasPlaying = !this.audio.paused && !this.audio.ended;
      this.currentReleaseIndex = index;
      const requestedTrack = Number.isInteger(options.trackIndex) ? options.trackIndex : 0;
      const clampedTrack = Math.min(Math.max(0, requestedTrack), Math.max(0, release.tracks.length - 1));
      this.currentTrackIndex = Number.isInteger(options.trackIndex)
        ? clampedTrack
        : this.firstAllowedTrackIndex(release, clampedTrack);

      this.releaseNodes.forEach((node, nodeIndex) => {
        const active = nodeIndex === index;
        node.classList.toggle('is-active', active);
        node.querySelector('[role="tab"]')?.setAttribute('aria-selected', String(active));
      });

      const targetLeft = release.node.offsetLeft - ((this.carousel.clientWidth - release.node.clientWidth) / 2);
      this.carousel.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth'
      });
      this.renderRelease();
      this.loadCurrentTrack(Boolean(options.autoplay || currentWasPlaying));

      if (options.openLibrary) this.setLibraryOpen(true);

      if (options.focusStage) {
        this.trackListHeading.setAttribute('tabindex', '-1');
        this.trackListHeading.focus({ preventScroll: true });
      }
    }

    renderRelease() {
      const release = this.currentRelease();
      if (!release) return;

      this.releaseTitle.textContent = release.title;
      this.releaseMeta.textContent = [release.type, release.date].filter(Boolean).join(' · ');
      this.trackListHeading.textContent = `${release.title} track list`;
      this.activeArt.src = release.cover || '';
      this.activeArt.alt = release.cover ? `${release.title} cover art` : '';
      this.activeArt.hidden = !release.cover;
      if (this.stageBackground && this.stageBackground.dataset.fixedBackground !== 'true') {
        this.stageBackground.src = release.cover || '';
        this.stageBackground.hidden = !release.cover;
      }
      this.insertOpenButton?.setAttribute('aria-label', `Open the ${release.title} virtual album insert`);
      this.insertHeading.textContent = release.title;
      this.insertReleaseTitle.textContent = release.title;
      this.insertMiniArt.src = release.cover || '';
      this.insertMiniArt.alt = release.cover ? `${release.title} cover art` : '';
      this.insertMiniArt.hidden = !release.cover;
      if (this.insertOpen) {
        this.insertPageIndex = 0;
        this.renderInsertSpread();
      }
      this.releaseNotes.innerHTML = release.notes;
      this.detailsToggle.hidden = !release.notes;
      this.releaseNotes.hidden = true;
      this.detailsToggle.setAttribute('aria-expanded', 'false');
      this.explicitToggle.setAttribute('aria-checked', String(this.explicitAllowed));
      this.releaseBrowserLabel.textContent = release.title;
      this.renderServices(release);
      this.renderTrackList(release);
      this.updateFeaturedCallout();
    }

    renderServices(release) {
      const links = [
        ['Apple Music', release.apple],
        ['Spotify', release.spotify],
        ['YouTube', release.youtube],
        [release.purchaseLabel, release.purchase]
      ].filter((entry) => entry[1]);

      this.serviceLinks.replaceChildren(...links.map(([label, url]) => {
        const anchor = document.createElement('a');
        anchor.className = 'jjc-player__service-link';
        anchor.href = url;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.textContent = label;
        return anchor;
      }));
    }

    renderTrackList(release) {
      const items = release.tracks.map((track, index) => {
        const item = document.createElement('li');
        item.className = 'jjc-player__track-item';
        item.classList.toggle('is-active', index === this.currentTrackIndex);
        item.classList.toggle('is-explicit-locked', track.explicit && !this.explicitAllowed);

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'jjc-player__track-button';
        const locked = track.explicit && !this.explicitAllowed;
        button.setAttribute('aria-label', locked
          ? `${track.title}. Explicit track locked; activate to review the content notice.`
          : `Play ${track.title}`);
        button.addEventListener('click', () => {
          if (locked) {
            this.pendingExplicitTrackIndex = index;
            this.openExplicitDialog();
            return;
          }
          this.currentTrackIndex = index;
          this.loadCurrentTrack(true);
          this.renderTrackList(release);
          this.setLibraryOpen(false, { restoreFocus: false });
        });

        const title = document.createElement('span');
        title.className = 'jjc-player__track-name';
        title.textContent = track.title;
        button.append(title);

        if (index === release.featuredTrack) {
          const featured = document.createElement('span');
          featured.className = 'jjc-player__featured-marker';
          featured.textContent = '★';
          featured.title = 'Featured track';
          featured.setAttribute('aria-label', 'Featured track');
          button.append(featured);
        }

        if (track.explicit) {
          const explicit = document.createElement('span');
          explicit.className = 'jjc-player__explicit';
          explicit.textContent = 'E';
          explicit.setAttribute('aria-label', 'Explicit');
          button.append(explicit);
        }

        item.append(button);
        return item;
      });

      this.trackList.replaceChildren(...items);
    }

    loadCurrentTrack(autoplay) {
      const release = this.currentRelease();
      const track = this.currentTrack();

      if (!track) {
        this.audio.removeAttribute('src');
        this.audio.load();
        this.trackTitleText.textContent = 'No playable tracks yet';
        this.trackPosition.textContent = '';
        this.playToggle.disabled = true;
        this.resetTimeline();
        return;
      }

      if (track.explicit && !this.explicitAllowed) {
        this.audio.pause();
        this.trackTitleText.textContent = 'Explicit track locked';
        this.trackPosition.textContent = 'Enable explicit content to play this track';
        this.playToggle.disabled = false;
        this.resetTimeline();
        if (autoplay) this.openExplicitDialog();
        return;
      }

      this.playToggle.disabled = false;
      this.trackTitleText.textContent = track.title;
      this.insertTrackTitle.textContent = track.title;
      this.trackPosition.textContent = `Track ${this.currentTrackIndex + 1} of ${release.tracks.length}`;
      this.insertTrackPosition.textContent = `Track ${this.currentTrackIndex + 1} of ${release.tracks.length}`;
      this.drawerNowPlaying.textContent = `Now playing: ${track.title}`;
      this.audio.src = track.url;
      this.audio.load();
      this.resetTimeline();
      this.renderTrackList(release);
      this.updateFeaturedCallout();
      this.updateMarquee();

      if (autoplay) {
        this.playbackIntentAt = Date.now();
        const playPromise = this.audio.play();
        if (playPromise?.catch) playPromise.catch(() => this.setPlayingState(false));
      }
    }

    updateFeaturedCallout() {
      const release = this.currentRelease();
      const featured = release?.tracks?.[release.featuredTrack];
      const show = Boolean(featured);
      const featuredIsCurrent = release?.featuredTrack === this.currentTrackIndex;
      this.featuredSlot.replaceChildren();
      if (!show) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'jjc-player__featured-callout';
      button.setAttribute('aria-label', `Play featured track ${featured.title}`);
      button.innerHTML = `
        <span class="jjc-player__featured-star" aria-hidden="true">★</span>
        <span><small>Featured track</small><strong></strong></span>
        <span class="jjc-player__featured-action">${featuredIsCurrent ? 'Restart' : 'Play'}</span>
      `;
      button.querySelector('strong').textContent = featured.title;
      button.addEventListener('click', () => {
        this.currentTrackIndex = release.featuredTrack;
        this.loadCurrentTrack(true);
      });
      this.featuredSlot.append(button);
    }

    setReleaseBrowserOpen(open, options = {}) {
      if (open) {
        this.setLibraryOpen(false, { restoreFocus: false });
        this.setInsertOpen(false, { restoreFocus: false });
      }
      this.releaseBrowserOpen = open;
      this.stage.classList.toggle('is-release-open', open);
      this.releaseBrowser?.classList.toggle('is-open', open);
      if (this.releaseBrowserPanel) this.releaseBrowserPanel.hidden = !open;
      this.releaseBrowserToggle?.setAttribute('aria-expanded', String(open));
      this.clearReleaseDrawerTimer();
      if (options.focus) {
        window.requestAnimationFrame(() => this.catalogSearch?.focus({ preventScroll: true }));
      }
    }

    filterCatalog(rawQuery) {
      const query = String(rawQuery || '').trim().toLowerCase();
      this.searchQuery = query;
      const results = [];

      if (query) {
        this.releases.forEach((release, releaseIndex) => {
          release.tracks.forEach((track, trackIndex) => {
            if (track.title.toLowerCase().includes(query)) {
              results.push({ release, releaseIndex, track, trackIndex, type: 'track' });
            }
          });
          const releaseText = [release.title, release.type, release.date].join(' ').toLowerCase();
          if (releaseText.includes(query) && !release.tracks.some((track) => track.title.toLowerCase().includes(query))) {
            results.push({ release, releaseIndex, trackIndex: 0, type: 'release' });
          }
        });
      }

      this.releaseCarousel.hidden = Boolean(query);
      this.searchResults.hidden = !query;
      this.searchResults.replaceChildren(...results.map((result) => this.createSearchResult(result)));

      this.searchClear.hidden = !query;
      this.searchEmpty.hidden = !query || results.length > 0;
      this.searchStatus.textContent = query
        ? `${results.length} ${results.length === 1 ? 'result' : 'results'} found.`
        : '';
      if (query) this.setReleaseBrowserOpen(true);
    }

    createSearchResult(result) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'jjc-player__search-card';
      button.setAttribute('aria-label', result.type === 'track'
        ? `Play ${result.track.title} from ${result.release.title}`
        : `Open ${result.release.title}`);
      if (result.release.cover) {
        const image = document.createElement('img');
        image.src = result.release.cover;
        image.alt = '';
        image.loading = 'lazy';
        button.append(image);
      }
      const shade = document.createElement('span');
      shade.className = 'jjc-player__search-card-shade';
      const meta = document.createElement('small');
      meta.textContent = result.type === 'track'
        ? `Track ${result.trackIndex + 1} · ${result.release.title}`
        : [result.release.type, result.release.date].filter(Boolean).join(' · ');
      const title = document.createElement('strong');
      title.textContent = result.type === 'track' ? result.track.title : result.release.title;
      shade.append(meta, title);
      button.append(shade);
      button.addEventListener('click', () => this.selectRelease(result.releaseIndex, {
        trackIndex: result.trackIndex,
        autoplay: result.type === 'track',
        fromUser: true
      }));
      button.addEventListener('click', () => this.setReleaseBrowserOpen(false));
      return button;
    }

    scheduleReleaseDrawerClose(options = {}) {
      this.clearReleaseDrawerTimer();
      if (!this.releaseBrowserAutoClose || !this.releaseBrowserOpen) return;
      this.releaseDrawerTimer = window.setTimeout(() => {
        if (options.requirePlaying === false || !this.audio.paused) this.setReleaseBrowserOpen(false);
      }, 3000);
    }

    clearReleaseDrawerTimer() {
      if (!this.releaseDrawerTimer) return;
      window.clearTimeout(this.releaseDrawerTimer);
      this.releaseDrawerTimer = null;
    }

    updateMarquee() {
      if (!this.trackTitle || !this.trackTitleText) return;
      this.trackTitle.classList.remove('is-overflowing');
      this.trackTitle.style.removeProperty('--jjc-marquee-distance');
      this.trackTitle.style.removeProperty('--jjc-marquee-duration');
      window.requestAnimationFrame(() => {
        const distance = Math.ceil(this.trackTitleText.scrollWidth - this.trackTitle.clientWidth);
        if (distance <= 2) return;
        this.trackTitle.style.setProperty('--jjc-marquee-distance', `${distance}px`);
        this.trackTitle.style.setProperty('--jjc-marquee-duration', `${Math.max(8, distance / 22 + 5)}s`);
        this.trackTitle.classList.add('is-overflowing');
      });
    }

    togglePlayback() {
      const track = this.currentTrack();
      if (!track) return;
      if (track.explicit && !this.explicitAllowed) {
        this.openExplicitDialog();
        return;
      }
      if (this.audio.paused) {
        this.stopInsertMemories({ resumeMain: false });
        this.playbackIntentAt = Date.now();
        const playPromise = this.audio.play();
        if (playPromise?.catch) playPromise.catch(() => this.setPlayingState(false));
      } else {
        this.audio.pause();
      }
    }

    previousTrack() {
      if (this.audio.currentTime > 3) {
        this.audio.currentTime = 0;
        return;
      }
      this.stepTrack(-1, !this.audio.paused);
    }

    nextTrack(forceAutoplay) {
      this.stepTrack(1, forceAutoplay || !this.audio.paused);
    }

    stepTrack(direction, autoplay) {
      const next = this.findNextPlayable(direction);
      if (!next) {
        this.setPlayingState(false);
        return;
      }

      const { releaseIndex, trackIndex } = next;

      this.currentReleaseIndex = releaseIndex;
      this.currentTrackIndex = trackIndex;
      this.releaseNodes.forEach((node, index) => {
        node.classList.toggle('is-active', index === releaseIndex);
        node.querySelector('[role="tab"]')?.setAttribute('aria-selected', String(index === releaseIndex));
      });
      this.renderRelease();
      this.loadCurrentTrack(autoplay);
    }

    findNextPlayable(direction) {
      const positions = [];
      this.releases.forEach((release, releaseIndex) => {
        release.tracks.forEach((track, trackIndex) => positions.push({ releaseIndex, trackIndex, track }));
      });
      if (!positions.length) return null;

      const currentPosition = positions.findIndex((position) =>
        position.releaseIndex === this.currentReleaseIndex && position.trackIndex === this.currentTrackIndex
      );
      const attempts = this.loopLibrary ? positions.length : positions.length - 1;
      for (let offset = 1; offset <= attempts; offset += 1) {
        const rawIndex = currentPosition + (offset * direction);
        if (!this.loopLibrary && (rawIndex < 0 || rawIndex >= positions.length)) return null;
        const wrappedIndex = (rawIndex + positions.length) % positions.length;
        const position = positions[wrappedIndex];
        if (this.explicitAllowed || !position.track.explicit) return position;
      }
      return null;
    }

    moveRelease(direction) {
      const next = (this.currentReleaseIndex + direction + this.releases.length) % this.releases.length;
      this.selectRelease(next, { autoplay: false, focusStage: false });
    }

    updateTimeline() {
      const duration = this.audio.duration;
      const current = this.audio.currentTime;
      const usable = Number.isFinite(duration) && duration > 0;
      this.progress.disabled = !usable;
      this.progress.value = usable ? String(Math.round((current / duration) * 1000)) : '0';
      this.currentTime.textContent = this.formatTime(current);
      this.duration.textContent = this.formatTime(duration);
    }

    resetTimeline() {
      this.progress.value = '0';
      this.progress.disabled = true;
      this.currentTime.textContent = '0:00';
      this.duration.textContent = '0:00';
    }

    setPlayingState(playing) {
      this.playIcon.hidden = playing;
      this.pauseIcon.hidden = !playing;
      this.playToggle.setAttribute('aria-label', playing ? 'Pause' : 'Play');
      if (this.insertPlayIcon && this.insertPauseIcon) {
        this.insertPlayIcon.hidden = playing;
        this.insertPauseIcon.hidden = !playing;
        this.insertPlay.setAttribute('aria-label', playing ? 'Pause' : 'Play');
      }
    }

    setLibraryOpen(open, options = {}) {
      const isCompactViewport = window.matchMedia('(max-width: 749px)').matches;
      const stageScrollTop = this.stage.scrollTop;
      const playerScrollTop = isCompactViewport ? 0 : (this.nowPlaying?.scrollTop || 0);
      if (open) {
        this.setReleaseBrowserOpen(false);
        this.setInsertOpen(false, { restoreFocus: false });
      }
      this.libraryOpen = open;
      this.stage.classList.toggle('is-library-open', open);
      this.libraryPanel.hidden = !open;
      this.libraryToggle.setAttribute('aria-expanded', String(open));
      if (!isCompactViewport) {
        if (open) this.libraryClose.focus({ preventScroll: true });
        else if (options.restoreFocus !== false) this.libraryToggle.focus({ preventScroll: true });
      }
      const restoreScroll = () => {
        this.stage.scrollTop = stageScrollTop;
        if (this.nowPlaying) this.nowPlaying.scrollTop = playerScrollTop;
      };
      restoreScroll();
      window.requestAnimationFrame(() => {
        restoreScroll();
        window.requestAnimationFrame(restoreScroll);
      });
      window.setTimeout(restoreScroll, 60);
      window.setTimeout(restoreScroll, 360);
      this.updateMarquee();
    }

    setInsertOpen(open, options = {}) {
      if (!this.insertPanel) return;
      if (open) {
        this.setReleaseBrowserOpen(false);
        this.setLibraryOpen(false, { restoreFocus: false });
        this.insertPageIndex = 0;
        this.renderInsertSpread();
      } else {
        this.closeInsertImage();
        this.stopInsertMemories({ resumeMain: true });
      }
      this.insertOpen = open;
      this.stage.classList.toggle('is-insert-open', open);
      this.insertPanel.hidden = !open;
      if (open) this.insertClose.focus({ preventScroll: true });
      else if (options.restoreFocus !== false) this.insertOpenButton?.focus({ preventScroll: true });
    }

    insertSlides() {
      const release = this.currentRelease();
      if (!release) return [];
      if (release.slides?.length) return release.slides;
      return [
        { kind: 'image', title: release.title, image: release.cover, content: '' },
        {
          kind: 'text',
          title: `Inside ${release.title}`,
          content: release.notes || '<p>This virtual insert is ready for artwork, stories, credits, and behind-the-song material.</p>'
        }
      ];
    }

    moveInsertPages(direction) {
      const slides = this.insertSlides();
      if (!slides.length) return;
      const step = window.matchMedia('(max-width: 749px)').matches ? 1 : 2;
      const maxStart = step === 1 ? slides.length - 1 : Math.max(0, Math.floor((slides.length - 1) / 2) * 2);
      const next = Math.min(maxStart, Math.max(0, this.insertPageIndex + (direction * step)));
      if (next === this.insertPageIndex) return;
      this.closeInsertImage();
      this.stopInsertMemories({ resumeMain: true });
      this.insertBook.classList.remove('is-moving-forward', 'is-moving-back', 'is-transitioning');
      void this.insertBook.offsetWidth;
      this.insertBook.classList.add(direction > 0 ? 'is-moving-forward' : 'is-moving-back', 'is-transitioning');
      window.setTimeout(() => {
        this.insertPageIndex = next;
        this.renderInsertSpread();
        window.requestAnimationFrame(() => this.insertBook.classList.remove('is-transitioning'));
      }, this.prefersReducedMotion() ? 0 : 145);
    }

    renderInsertSpread() {
      const slides = this.insertSlides();
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const left = slides[this.insertPageIndex];
      const right = mobile ? null : slides[this.insertPageIndex + 1];
      this.renderInsertPage(this.insertLeft, left, this.insertPageIndex);
      this.renderInsertPage(this.insertRight, right, this.insertPageIndex + 1);
      this.insertRight.classList.toggle('is-empty', !right);
      this.insertPrevious.disabled = this.insertPageIndex === 0;
      this.insertNext.disabled = this.insertPageIndex + (mobile ? 1 : 2) >= slides.length;
      const visibleEnd = Math.min(slides.length, this.insertPageIndex + (mobile ? 1 : 2));
      this.insertPageCount.textContent = mobile || visibleEnd === this.insertPageIndex + 1
        ? `${this.insertPageIndex + 1} / ${slides.length}`
        : `${this.insertPageIndex + 1}–${visibleEnd} / ${slides.length}`;
    }

    renderInsertPage(container, slide, index) {
      container.replaceChildren();
      if (!slide) return;
      container.dataset.kind = slide.kind;

      const buildCopy = () => {
        const copy = document.createElement('div');
        copy.className = 'jjc-player__book-copy';
        if (slide.title) {
          const title = document.createElement('h5');
          title.textContent = slide.title;
          copy.append(title);
        }
        return copy;
      };

      if (slide.kind === 'image' && slide.image) {
        const image = document.createElement('img');
        image.src = slide.image;
        image.alt = slide.title || `Insert page ${index + 1}`;
        image.loading = 'lazy';
        image.tabIndex = 0;
        image.role = 'button';
        image.setAttribute('aria-label', `Enlarge ${image.alt}`);
        image.addEventListener('click', () => this.openInsertImage(image.src, image.alt));
        image.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.openInsertImage(image.src, image.alt);
          }
        });
        container.append(image);
      } else if (slide.kind === 'video' && slide.video) {
        const frame = document.createElement('iframe');
        frame.src = this.videoEmbedUrl(slide.video);
        frame.title = slide.title || `Video insert page ${index + 1}`;
        frame.loading = 'lazy';
        frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        frame.allowFullscreen = true;
        container.append(frame);
      } else if (slide.kind === 'audio' && slide.audio) {
        const copy = buildCopy();
        const audio = document.createElement('audio');
        audio.src = slide.audio;
        audio.controls = true;
        audio.preload = 'metadata';
        audio.controlsList = 'nodownload noplaybackrate';
        audio.disableRemotePlayback = true;
        audio.addEventListener('contextmenu', (event) => event.preventDefault());
        audio.addEventListener('play', () => this.handleInsertMemoryPlay(audio));
        audio.addEventListener('ended', () => this.finishInsertMemory(audio));
        copy.append(audio);
        if (slide.content) {
          const content = document.createElement('div');
          content.className = 'rte';
          content.innerHTML = slide.content;
          copy.append(content);
        }
        container.append(copy);
        return;
      }

      if (slide.title || slide.content) {
        const copy = buildCopy();
        if (slide.content) {
          const content = document.createElement('div');
          content.className = 'rte';
          content.innerHTML = slide.content;
          copy.append(content);
        }
        container.append(copy);
      }
    }

    handleInsertMemoryPlay(memoryAudio) {
      this.insertPanel.querySelectorAll('audio').forEach((other) => {
        if (other !== memoryAudio && !other.paused) {
          other.dataset.resumeMain = 'false';
          other.pause();
        }
      });
      const mainWasPlaying = !this.audio.paused && !this.audio.ended;
      memoryAudio.dataset.resumeMain = String(mainWasPlaying);
      if (mainWasPlaying) this.audio.pause();
    }

    finishInsertMemory(memoryAudio) {
      const shouldResume = memoryAudio.dataset.resumeMain === 'true';
      memoryAudio.dataset.resumeMain = 'false';
      if (!shouldResume || !this.currentTrack()) return;
      this.playbackIntentAt = Date.now();
      const playPromise = this.audio.play();
      if (playPromise?.catch) playPromise.catch(() => this.setPlayingState(false));
    }

    openInsertImage(src, alt) {
      if (!this.insertImageViewer || !this.insertImageFull || !src) return;
      if (this.insertImageViewer.parentElement !== document.body) document.body.append(this.insertImageViewer);
      this.insertImageFull.src = src;
      this.insertImageFull.alt = alt || 'Enlarged insert image';
      this.insertImageFull.classList.remove('is-zoomed');
      this.insertImageViewer.hidden = false;
      document.documentElement.classList.add('jjc-insert-image-open');
      this.insertImageViewer.querySelector('[data-insert-image-close]:not(.jjc-player__image-viewer-backdrop)')?.focus({ preventScroll: true });
    }

    closeInsertImage() {
      if (!this.insertImageViewer || this.insertImageViewer.hidden) return;
      this.insertImageViewer.hidden = true;
      this.insertImageFull?.classList.remove('is-zoomed');
      document.documentElement.classList.remove('jjc-insert-image-open');
    }

    stopInsertMemories(options = {}) {
      let shouldResume = false;
      this.insertPanel?.querySelectorAll('audio').forEach((memoryAudio) => {
        shouldResume ||= memoryAudio.dataset.resumeMain === 'true';
        memoryAudio.dataset.resumeMain = 'false';
        memoryAudio.pause();
      });
      if (shouldResume && options.resumeMain) {
        this.playbackIntentAt = Date.now();
        const playPromise = this.audio.play();
        if (playPromise?.catch) playPromise.catch(() => this.setPlayingState(false));
      }
    }

    videoEmbedUrl(rawUrl) {
      try {
        const url = new URL(rawUrl, window.location.origin);
        if (url.hostname.includes('youtu.be')) return `https://www.youtube-nocookie.com/embed/${url.pathname.slice(1)}`;
        if (url.hostname.includes('youtube.com')) return `https://www.youtube-nocookie.com/embed/${url.searchParams.get('v') || ''}`;
        if (url.hostname.includes('vimeo.com')) return `https://player.vimeo.com/video/${url.pathname.split('/').filter(Boolean).pop()}`;
      } catch (error) {
        return '';
      }
      return '';
    }

    resetOverlays() {
      this.clearReleaseDrawerTimer();
      this.stopInsertMemories({ resumeMain: false });
      if (this.explicitDialog) this.explicitDialog.hidden = true;
      if (this.insertPanel) this.insertPanel.hidden = true;
      if (this.insertImageViewer) this.insertImageViewer.hidden = true;
      if (this.libraryPanel) this.libraryPanel.hidden = true;
      this.stage?.classList.remove('is-insert-open', 'is-library-open');
      this.insertOpen = false;
      this.libraryOpen = false;
    }

    readExplicitPreference() {
      try {
        return window.localStorage.getItem('jjc_explicit_content_allowed') === 'true';
      } catch (error) {
        return false;
      }
    }

    setExplicitAllowed(allowed) {
      this.explicitAllowed = allowed;
      try {
        window.localStorage.setItem(this.explicitPreferenceKey, String(allowed));
      } catch (error) {
        // Playback still works when storage is unavailable.
      }
      this.explicitToggle.setAttribute('aria-checked', String(allowed));

      const track = this.currentTrack();
      if (!allowed && track?.explicit) {
        this.audio.pause();
        this.currentTrackIndex = this.firstAllowedTrackIndex(this.currentRelease(), this.currentTrackIndex);
        this.loadCurrentTrack(false);
      }
      this.renderTrackList(this.currentRelease());
    }

    firstAllowedTrackIndex(release, preferredIndex = 0) {
      if (!release?.tracks.length || this.explicitAllowed) return preferredIndex;
      for (let offset = 0; offset < release.tracks.length; offset += 1) {
        const index = (preferredIndex + offset) % release.tracks.length;
        if (!release.tracks[index].explicit) return index;
      }
      return preferredIndex;
    }

    openExplicitDialog() {
      this.dialogReturnFocus = document.activeElement;
      this.explicitDialog.hidden = false;
      this.explicitConfirm.focus({ preventScroll: true });
    }

    closeExplicitDialog() {
      this.explicitDialog.hidden = true;
      this.pendingExplicitTrackIndex = null;
      this.dialogReturnFocus?.focus?.({ preventScroll: true });
    }

    currentRelease() {
      return this.releases[this.currentReleaseIndex];
    }

    currentTrack() {
      return this.currentRelease()?.tracks[this.currentTrackIndex];
    }

    formatTime(seconds) {
      if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
      const minutes = Math.floor(seconds / 60);
      const remainder = Math.floor(seconds % 60).toString().padStart(2, '0');
      return `${minutes}:${remainder}`;
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('jjc-music-player', JJCMusicPlayer);
}
